import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaRocket, FaHandshake, FaHandHoldingUsd, FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa';
import SEO from '../components/SEO/SEO';

const InvestorInsightsPage = () => {
  const seoData = {
    title: "Investor Insights 2025 - Startup Trailblazers | Dhyon Technologies",
    description: "Discover the 20 trailblazing startups igniting the future of tech, health, and finance in 2025. Expert analysis on AI, Healthtech, and Fintech trends.",
    keywords: "Investor Insights, Startup Trends 2025, AI Startups, Healthtech, Fintech, Venture Capital"
  };

  const startups = [
    { id: 'perplexity-ai', name: "Perplexity AI", sector: "AI/Search", location: "San Francisco, CA", metric: "200+ employees", why: "Real-time LLM search; 5,000% growth." },
    { id: 'suno', name: "Suno", sector: "AI/Music", location: "Cambridge, MA", metric: "$125M Series B", why: "Text-to-song revolution." },
    { id: 'cohere', name: "Cohere", sector: "AI/Enterprise", location: "Toronto, ON", metric: "$940M Series D", why: "Custom NLP for biz." },
    { id: 'charlie-health', name: "Charlie Health", sector: "Healthtech", location: "Bozeman, MT", metric: "2,200 employees", why: "Virtual mental health scale." },
    { id: 'cadence', name: "Cadence", sector: "Healthtech", location: "New York, NY", metric: "370 employees", why: "Chronic care monitoring." },
    { id: 'synctera', name: "Synctera", sector: "Fintech", location: "San Francisco, CA", metric: "80 employees", why: "BaaS for embedded finance." },
    { id: 'bilt-rewards', name: "Bilt Rewards", sector: "Fintech", location: "New York, NY", metric: "$563M total", why: "Rent loyalty innovation." },
    { id: 'webai', name: "webAI", sector: "AI/Edge", location: "Austin, TX", metric: "$43M Series A", why: "Privacy-first device AI." },
    { id: 'overjet', name: "Overjet", sector: "Healthtech", location: "Boston, MA", metric: "$134M Series C", why: "AI dental diagnostics." },
    { id: 'multifactor', name: "Multifactor", sector: "AI/Security (YC)", location: "YC Fall 2025", metric: "-", why: "Zero-trust for AI agents." },
    { id: 'lightberry', name: "Lightberry", sector: "Robotics (YC)", location: "YC Fall 2025", metric: "-", why: "Humanoid social AI." },
    { id: 'unsiloed-ai', name: "Unsiloed AI", sector: "AI/Data (YC)", location: "YC Fall 2025", metric: "-", why: "Unstructured data APIs." },
    { id: 'eva', name: "Eva", sector: "AI/Compute", location: "Cambridge, MA", metric: "MIT Spinout", why: "99% cheaper training." },
    { id: 'gaia-ai', name: "Gaia AI", sector: "AI/Climate", location: "Cambridge, MA", metric: "MIT Spinout", why: "Forest mapping for wildfires." },
    { id: 'zip', name: "Zip", sector: "Fintech/Procure", location: "San Francisco, CA", metric: "$371M total", why: "AI spend automation." },
    { id: 'unit', name: "Unit", sector: "Fintech/Embedded", location: "New York, NY", metric: "$388M total", why: "Banking APIs." },
    { id: 'hypercubic', name: "Hypercubic", sector: "AI/Legacy (YC)", location: "YC Fall 2025", metric: "-", why: "COBOL modernization." },
    { id: 'parrot', name: "Parrot", sector: "Edtech (YC)", location: "YC Fall 2025", metric: "-", why: "TikTok-style languages." },
    { id: 'boom-ai', name: "Boom AI", sector: "E-comm AI (YC)", location: "YC Fall 2025", metric: "-", why: "24/7 sales agents." },
    { id: 'vocadian', name: "Vocadian", sector: "AI/Safety", location: "Cambridge, MA", metric: "MIT Spinout", why: "Voice fatigue prediction." }
  ];

  return (
    <div className="bg-white min-h-screen">
      <SEO {...seoData} />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-purple-900 text-white">
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.3),transparent_70%)]"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <Link to="/startup" className="inline-flex items-center text-blue-300 hover:text-white mb-8 transition-colors">
            <FaArrowLeft className="mr-2" />
            Back to Startup India
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Investor Insights
            </h1>
            <h2 className="text-2xl md:text-3xl font-medium text-blue-200 mb-8">
              2025's Startup Fireworks: 20 Trailblazers Igniting the Future of Tech, Health, and Finance
            </h2>
            <p className="text-lg text-blue-100 leading-relaxed max-w-3xl mx-auto">
              As 2025 draws to a close, the startup world has been a whirlwind of breakthroughs. 
              Our comprehensive guide explores the most impactful innovations driving the next wave of unicorns.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none text-gray-700 leading-relaxed mb-16"
          >
            <p>
              Research suggests AI continues to dominate, powering over 60% of venture funding, but healthtech and sustainable fintech are surging as counterweights to economic uncertainties. It seems likely that these sectors will drive the next wave of unicorns, though regulatory hurdles in AI ethics and data privacy add layers of complexity. The evidence leans toward a resilient ecosystem where cross-border collaborations—like those from YC's Fall batch—foster empathy-driven solutions amid global challenges.
            </p>
          </motion.div>

          {/* Why 2025 Stood Out */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20 bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-100 shadow-sm"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Why 2025 Stood Out for Startups</h3>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  This year, venture capital hit $150B globally, with AI and climate tech capturing the lion's share, per year-end reports. Yet, bootstrapped gems in defence and wellness highlight capital efficiency as a survival edge.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  These aren't just hype—startups here scaled 200-500% in users or revenue, drawing talent magnets like LinkedIn's top growth lists.
                </p>
              </div>
              <ul className="space-y-4">
                {[
                  { title: "AI Ubiquity", desc: "Tools for everything from music creation to bridge monitoring." },
                  { title: "Health & Wellness Boom", desc: "Virtual care and predictive analytics addressing gaps." },
                  { title: "Fintech Resilience", desc: "Embedded finance and crypto integrations thriving." },
                  { title: "Emerging Edges", desc: "Robotics and cleantech tackling labor and energy crises." }
                ].map((trend, idx) => (
                  <li key={idx} className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                    <span className="flex-shrink-0 w-2 h-2 mt-2.5 bg-blue-600 rounded-full"></span>
                    <div>
                      <h4 className="font-bold text-gray-900">{trend.title}</h4>
                      <p className="text-gray-600">{trend.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Spotlight Grid */}
          <div className="grid lg:grid-cols-3 gap-8 mb-20">
            {/* AI Spotlight */}
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6">
                <FaRocket className="text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI Powerhouses</h3>
              <p className="text-gray-600 mb-6">Autonomous agents and enterprise models disrupting entire industries.</p>
              <ul className="space-y-3">
                <li className="font-medium text-blue-600">● Perplexity AI (Search)</li>
                <li className="font-medium text-blue-600">● Suno (Music Gen)</li>
                <li className="font-medium text-blue-600">● Cohere (Enterprise NLP)</li>
              </ul>
            </motion.div>

            {/* Health Spotlight */}
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100"
            >
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-6">
                <FaHandshake className="text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Healthtech Heroes</h3>
              <p className="text-gray-600 mb-6">Healing the divide with virtual therapy and remote monitoring.</p>
              <ul className="space-y-3">
                <li className="font-medium text-purple-600">● Charlie Health (Therapy)</li>
                <li className="font-medium text-purple-600">● Cadence (Remote Care)</li>
                <li className="font-medium text-purple-600">● Overjet (Dental AI)</li>
              </ul>
            </motion.div>

            {/* Fintech Spotlight */}
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100"
            >
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mb-6">
                <FaHandHoldingUsd className="text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Fintech Frontiers</h3>
              <p className="text-gray-600 mb-6">Embedded finance and rewards making money moves smarter.</p>
              <ul className="space-y-3">
                <li className="font-medium text-green-600">● Synctera (Embedded Bank)</li>
                <li className="font-medium text-green-600">● Bilt Rewards (Rent)</li>
                <li className="font-medium text-green-600">● Zip (Spend Automation)</li>
              </ul>
            </motion.div>
          </div>

          {/* Detailed Table */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">The Aggregated Watchlist</h3>
              <p className="text-gray-600">20 Must-Know Trailblazers for 2026. Click any row for deep-dive data.</p>
            </div>
            
            <div className="overflow-x-auto rounded-3xl shadow-2xl border border-gray-200 bg-white">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="px-6 py-5 font-bold uppercase text-sm tracking-wider">Startup</th>
                    <th className="px-6 py-5 font-bold uppercase text-sm tracking-wider">Sector</th>
                    <th className="px-6 py-5 font-bold uppercase text-sm tracking-wider">Location</th>
                    <th className="px-6 py-5 font-bold uppercase text-sm tracking-wider text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {startups.map((startup) => (
                    <tr 
                      key={startup.id}
                      className="group hover:bg-blue-50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-5">
                        <Link to={`/investor-insights/${startup.id}`} className="block">
                          <span className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {startup.name}
                          </span>
                        </Link>
                      </td>
                      <td className="px-6 py-5 text-gray-600">{startup.sector}</td>
                      <td className="px-6 py-5 text-gray-600">{startup.location}</td>
                      <td className="px-6 py-5 text-right">
                        <Link 
                          to={`/investor-insights/${startup.id}`}
                          className="inline-flex items-center text-blue-600 font-semibold group-hover:underline"
                        >
                          Details
                          <FaExternalLinkAlt className="ml-2 text-xs" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Closing Thoughts */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-[2rem] p-10 md:p-16 text-white text-center shadow-xl"
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-8">Looking Ahead: Your Move</h3>
            <p className="text-xl leading-relaxed mb-6 opacity-90 max-w-3xl mx-auto">
              As 2025 fades, these startups embody hope amid uncertainty. 
              The future isn't coming—it's being built. Which innovation will you back?
            </p>
            <div className="flex justify-center gap-6 mt-12">
              <Link to="/contact" className="px-8 py-4 bg-white text-blue-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                Consult Our Experts
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InvestorInsightsPage;
